import React, { useState, useCallback } from 'react';
import styles from './adminStyles.module.css';
import ProductTable from './ProductTable';
import ProductForm from './ProductForm';
import {
  validateToken,
  fetchProductsFromGitHub,
  saveProductsToGitHub,
  reorderWithStars,
  generateId,
} from './githubApi';

function AdminDashboard() {
  // Auth state
  const [token, setToken] = useState(
    () => sessionStorage.getItem('admin_token') || ''
  );
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!sessionStorage.getItem('admin_token')
  );
  const [username, setUsername] = useState(
    () => sessionStorage.getItem('admin_user') || ''
  );
  const [loginInput, setLoginInput] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Data state
  const [products, setProducts] = useState([]);
  const [sha, setSha] = useState('');
  const [starredIds, setStarredIds] = useState([]);
  const [loading, setLoading] = useState(false);

  // UI state
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const showStatus = (type, message) => {
    setStatusMessage({ type, message });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  // ---- Auth ----
  const handleLogin = async () => {
    if (!loginInput.trim()) return;
    setLoginLoading(true);
    setLoginError('');
    try {
      const user = await validateToken(loginInput.trim());
      setToken(loginInput.trim());
      setUsername(user);
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_token', loginInput.trim());
      sessionStorage.setItem('admin_user', user);
      // Load products after login
      loadProducts(loginInput.trim());
    } catch (err) {
      setLoginError(err.message || 'Authentication failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken('');
    setUsername('');
    setProducts([]);
    setSha('');
    setStarredIds([]);
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
  };

  // ---- Data ----
  const loadProducts = useCallback(async (authToken) => {
    setLoading(true);
    try {
      const result = await fetchProductsFromGitHub(authToken || token);
      setProducts(result.products);
      setSha(result.sha);
      // Auto-detect currently starred: first 8 products
      const initialStarred = result.products.slice(0, 8).map((p) => p._id);
      setStarredIds(initialStarred);
    } catch (err) {
      showStatus('error', 'שגיאה בטעינת מוצרים: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Auto-load if already authenticated
  React.useEffect(() => {
    if (isAuthenticated && products.length === 0 && token) {
      loadProducts(token);
    }
  }, [isAuthenticated, token, products.length, loadProducts]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const reordered = reorderWithStars(products, starredIds);
      // Use current sha (fetched during loadProducts)
      const newSha = await saveProductsToGitHub(token, reordered, sha);
      setSha(newSha);
      setProducts(reordered);
      const newStarred = reordered.slice(0, 8).map((p) => p._id);
      setStarredIds(newStarred);
      showStatus('success', '✅ המוצרים נשמרו בהצלחה!');
    } catch (err) {
      showStatus('error', 'שגיאה בשמירה: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ---- CRUD ----
  const handleToggleStar = (productId) => {
    setStarredIds((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      }
      if (prev.length >= 8) return prev;
      return [...prev, productId];
    });
  };

  const handleAddProduct = (formData) => {
    const newProduct = {
      _id: generateId(),
      ...formData,
    };
    setProducts((prev) => [...prev, newProduct]);
    setShowForm(false);
    showStatus('success', '✅ המוצר התווסף - לחצי שמירה לעדכון האתר');
  };

  const handleEditProduct = (formData) => {
    setProducts((prev) =>
      prev.map((p) =>
        p._id === editingProduct._id ? { ...p, ...formData } : p
      )
    );
    setEditingProduct(null);
    setShowForm(false);
    showStatus('success', '✅ מוצר התעדכן - לחצי שמירה לעדכון האתר');
  };

  const handleDeleteProduct = (product) => {
    setProducts((prev) => prev.filter((p) => p._id !== product._id));
    setStarredIds((prev) => prev.filter((id) => id !== product._id));
    setDeleteConfirm(null);
    showStatus('success', '✅ המוצר נחמק - לחצי שמירה לעדכון האתר');
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const openAdd = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  // ---- Login Screen ----
  if (!isAuthenticated) {
    return (
      <div className={styles.loginContainer}>
        <div className={styles.loginCard}>
          <h1 className={styles.loginTitle}>🔐 Admin page</h1>
          <p className={styles.loginSubtitle}>
            הכנס סיסמה
          </p>
          <input
            className={styles.loginInput}
            type="password"
            value={loginInput}
            onChange={(e) => setLoginInput(e.target.value)}
            placeholder="xxxxxxxxxxxx"
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />
          <button
            className={styles.loginButton}
            onClick={handleLogin}
            disabled={loginLoading || !loginInput.trim()}
          >
            {loginLoading ? 'מאשר...' : 'התחברות'}
          </button>
          {loginError && (
            <p className={styles.loginError}>{loginError}</p>
          )}
        </div>
      </div>
    );
  }

  // ---- Loading ----
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p className={styles.loadingText}>Loading products...</p>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div className={styles.dashboardContainer}>
      {/* Header */}
      <div className={styles.dashboardHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.headerTitle}>Admin Dashboard</h1>
          <span className={styles.headerBadge}>מוצרים</span>
        </div>
        <div className={styles.headerRight}>
          <span className={styles.userLabel}>👤 {username}</span>
          <button className={styles.logoutButton} onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={styles.contentArea}>
        {/* Status Messages */}
        {statusMessage && (
          <div
            className={`${styles.statusMessage} ${statusMessage.type === 'success'
              ? styles.statusSuccess
              : styles.statusError
              }`}
          >
            {statusMessage.message}
          </div>
        )}

        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.starCounter}>
            ⭐ מוצרים המופיעים בדף הבית:{' '}
            <span
              className={
                starredIds.length >= 8
                  ? styles.starCounterFull
                  : styles.starCounterHighlight
              }
            >
              {starredIds.length}/8
            </span>
          </div>
          <div className={styles.buttonGroup}>
            <a
              href="https://www.fxp.co.il/upload.php"
              target="_blank"
              rel="noreferrer"
              className={styles.linkButton}
            >
              🖼️  יצירת לינק לתמונה
            </a>
            <button className={styles.addButton} onClick={openAdd}>
              ➕ הוספת מוצר
            </button>
            <button
              className={styles.saveButton}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '⏳ שומר...' : '💾 שמירה'}
            </button>
          </div>
        </div>

        {/* Product Table */}
        <ProductTable
          products={products}
          starredIds={starredIds}
          onToggleStar={handleToggleStar}
          onEdit={openEdit}
          onDelete={(product) => setDeleteConfirm(product)}
        />
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <ProductForm
          product={editingProduct}
          onSave={editingProduct ? handleEditProduct : handleAddProduct}
          onCancel={closeForm}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmBox}>
            <p className={styles.confirmText}>
              את בטוחה שאת רוצה למחוק את המוצר <strong>"{deleteConfirm.name}"</strong>?
            </p>
            <div className={styles.confirmButtons}>
              <button
                className={styles.confirmCancel}
                onClick={() => setDeleteConfirm(null)}
              >
                ביטול
              </button>
              <button
                className={styles.confirmDelete}
                onClick={() => handleDeleteProduct(deleteConfirm)}
              >
                מחיקה
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
